import { forceLogout } from "./forceLogout";

class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired");
    this.name = "SessionExpiredError";
  }
}

/**
 * Wraps native `fetch`.  Returns the same Response object but with two
 * extra helpers:
 *   - `response._data`       – the already-parsed JSON body
 *   - `response.jsonData()`  – returns `response._data` (no re-parsing)
 *
 * @param {string | Request} url
 * @param {RequestInit}       [options]
 * @returns {Promise<Response & { _data: any, jsonData: () => any }>}
 */
const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, options);

  // Clone so we can read the body without consuming the original stream
  const cloned = response.clone();
  let data;

  try {
    data = await cloned.json();
  } catch {
    // Non-JSON response — skip session check, return raw response
    return response;
  }

  // Check for the specific session-expired payload
  if (
    data?.code === 401 &&
    data?.message === "Your session has expired"
  ) {
    forceLogout();
    throw new SessionExpiredError();
  }

  // Attach parsed data to avoid a second `.json()` parse by callers
  response._data = data;
  response.jsonData = () => data;

  return response;
};

export default apiFetch;
export { SessionExpiredError };
